import {
  InternalUser,
  InternalUserFilters,
  InternalUserInput,
  InternalUserOption,
  createInternalUser,
  deleteInternalUser,
  listInternalUsers,
  searchInternalUserCandidates,
  updateInternalUser,
} from '../../api/internalUserApi'
import NavBar from '../../components/nav-bar/nav-bar'
import { getDateTime } from '../../helpers/utils'
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd'
import { useCallback, useEffect, useState } from 'react'

type Editor = InternalUser | 'new' | null

const InternalUsersPage = () => {
  const [internalUsers, setInternalUsers] = useState<InternalUser[]>([])
  const [filters, setFilters] = useState<InternalUserFilters>({})
  const [editor, setEditor] = useState<Editor>(null)
  const [userOptions, setUserOptions] = useState<InternalUserOption[]>([])
  const [loading, setLoading] = useState(false)
  const [filterForm] = Form.useForm<InternalUserFilters>()
  const [editorForm] = Form.useForm<InternalUserInput>()

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      setInternalUsers(await listInternalUsers(filters))
    } catch (error: any) {
      notification.error({ title: '内部用户加载失败', description: error?.message })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    reload()
  }, [reload])

  const searchUsers = async (query: string) => {
    try {
      setUserOptions(await searchInternalUserCandidates(query))
    } catch (error: any) {
      notification.error({ title: '用户搜索失败', description: error?.message })
    }
  }

  const openEditor = (nextEditor: Exclude<Editor, null>) => {
    editorForm.resetFields()
    if (nextEditor === 'new') {
      setUserOptions([])
      editorForm.setFieldsValue({ isActive: true })
    } else {
      setUserOptions([{ id: nextEditor.userId, displayName: nextEditor.userDisplayName }])
      editorForm.setFieldsValue({
        userId: nextEditor.userId,
        internalUserName: nextEditor.internalUserName,
        isActive: nextEditor.isActive,
      })
    }
    setEditor(nextEditor)
  }

  const save = async () => {
    const values = await editorForm.validateFields()
    try {
      if (editor === 'new') {
        await createInternalUser(values)
      } else if (editor) {
        await updateInternalUser(editor.id, values)
      }
      setEditor(null)
      await reload()
      notification.success({ title: '内部用户已保存' })
    } catch (error: any) {
      notification.error({ title: '内部用户保存失败', description: error?.message })
    }
  }

  const remove = (internalUser: InternalUser) => {
    Modal.confirm({
      title: `删除内部用户 ${internalUser.userDisplayName}？`,
      content: '删除后，该用户下次登录将不再具有内部用户权限。',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteInternalUser(internalUser.id)
          await reload()
          notification.success({ title: '内部用户已删除' })
        } catch (error: any) {
          notification.error({ title: '内部用户删除失败', description: error?.message })
        }
      },
    })
  }

  return (
    <Space className="dashboard-wrapper" orientation="vertical">
      <NavBar title="内部用户" />
      <Card>
        <Form
          form={filterForm}
          layout="inline"
          onFinish={values => setFilters(values)}
          initialValues={filters}
        >
          <Form.Item name="userId" label="用户 ID">
            <InputNumber min={1} />
          </Form.Item>
          <Form.Item name="internalUserName" label="内部用户名">
            <Input />
          </Form.Item>
          <Form.Item name="isActive" label="状态">
            <Select
              allowClear
              options={[
                { label: '启用', value: true },
                { label: '停用', value: false },
              ]}
              style={{ width: 120 }}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                查询
              </Button>
              <Button
                onClick={() => {
                  filterForm.resetFields()
                  setFilters({})
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
      <Card>
        <Button type="primary" onClick={() => openEditor('new')}>
          新增内部用户
        </Button>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={internalUsers}
          pagination={{ pageSize: 20 }}
          columns={[
            { title: '用户', dataIndex: 'userDisplayName' },
            { title: '内部用户名', dataIndex: 'internalUserName' },
            {
              title: '状态',
              dataIndex: 'isActive',
              render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'default'}>{isActive ? '启用' : '停用'}</Tag>
              ),
            },
            {
              title: '创建时间',
              dataIndex: 'createdAt',
              render: (value: string) => getDateTime(value),
            },
            {
              title: '更新时间',
              dataIndex: 'updatedAt',
              render: (value: string) => getDateTime(value),
            },
            {
              title: '操作',
              render: (_: unknown, internalUser: InternalUser) => (
                <Space>
                  <Button onClick={() => openEditor(internalUser)}>编辑</Button>
                  <Button danger onClick={() => remove(internalUser)}>
                    删除
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editor === 'new' ? '新增内部用户' : '编辑内部用户'}
        open={editor !== null}
        onCancel={() => setEditor(null)}
        onOk={save}
        destroyOnHidden
      >
        <Form form={editorForm} layout="vertical">
          <Form.Item name="userId" label="用户" rules={[{ required: true, message: '请选择用户' }]}>
            <Select
              showSearch={{
                filterOption: false,
                onSearch: searchUsers,
              }}
              options={userOptions.map(user => ({ value: user.id, label: user.displayName }))}
              placeholder="搜索姓名、邮箱或用户 ID"
            />
          </Form.Item>
          <Form.Item name="internalUserName" label="内部用户名">
            <Input />
          </Form.Item>
          <Form.Item name="isActive" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}

export default InternalUsersPage
